/**
 * Every limit applied to untrusted input, in one place.
 *
 * Scattered magic numbers become inconsistent magic numbers, and a limit nobody can find is a
 * limit nobody can review. The values below are deliberately generous for real exams and
 * deliberately far below what would hurt the browser.
 */

export type AllowedImageMime = 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif';

export const ALLOWED_IMAGE_MIME_TYPES: readonly AllowedImageMime[] = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
];

export interface Limits {
  /** Hard cap on the file, checked before a single byte is read. */
  readonly maxFileBytes: number;
  /** Maximum nesting of objects and arrays, checked before `JSON.parse`. */
  readonly maxDepth: number;
  /** Maximum structural tokens, checked before `JSON.parse`. */
  readonly maxNodes: number;

  readonly maxQuestions: number;
  readonly maxOptionsPerQuestion: number;
  readonly maxStringLength: number;

  readonly maxEmbeddedAssetBytes: number;
  readonly maxTotalEmbeddedBytes: number;
  /** Largest width or height, in pixels, an embedded image may declare. */
  readonly maxImageDimension: number;
  /** Largest total pixels an embedded image may ask to be held, counting every frame. */
  readonly maxImagePixels: number;

  readonly allowedFileExtensions: readonly string[];
  readonly allowedImageMimeTypes: readonly AllowedImageMime[];
  readonly allowedUrlProtocols: readonly string[];
  readonly maxUrlLength: number;
}

export const DEFAULT_LIMITS: Limits = {
  // 25 MB. A text-only exam with 5000 questions lands around 10 MB; anything larger is either
  // embedded media (which has its own budget below) or an attempt to exhaust the tab.
  maxFileBytes: 25 * 1024 * 1024,

  // A valid exam nests about 6 levels deep. 32 leaves enormous headroom while still stopping the
  // deeply-nested-array payload that makes JSON.parse allocate for seconds.
  maxDepth: 32,

  // Structural tokens, not values — see preScan. Two million comfortably covers 5000 questions.
  maxNodes: 2_000_000,

  maxQuestions: 5000,
  // 26 is not arbitrary: it is the point past which single-letter display labels run out, which is
  // a good sign the question needs splitting.
  maxOptionsPerQuestion: 26,
  maxStringLength: 20_000,

  maxEmbeddedAssetBytes: 2 * 1024 * 1024,
  maxTotalEmbeddedBytes: 16 * 1024 * 1024,

  // A byte limit bounds the *compressed* file, and compression is what an attacker controls: a
  // 40 kB PNG can declare itself 30000x30000, and nothing has gone wrong until the browser is asked
  // to draw it and allocates several gigabytes. These bound what it will actually be asked to draw.
  //
  // 10000 on a side is far past any diagram or screenshot an exam has a use for, and 40 megapixels
  // is more than a full-frame camera produces. The pixel budget is multiplied by the frame count,
  // so an animation pays for every frame it asks to be held.
  maxImageDimension: 10_000,
  maxImagePixels: 40_000_000,

  allowedFileExtensions: ['.json'],
  allowedImageMimeTypes: ALLOWED_IMAGE_MIME_TYPES,
  // No http:, no data:, no blob:, and above all no javascript:. Exam files link to references;
  // they do not get to choose a URL scheme.
  allowedUrlProtocols: ['https:'],
  maxUrlLength: 2048,
};

/** Test and worker helper: overrides on top of the defaults, never a partial standalone config. */
export function withLimits(overrides: Partial<Limits>): Limits {
  return { ...DEFAULT_LIMITS, ...overrides };
}
